"use client";
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { photographedSampleVariants, sampleProductLink, sortSampleVariants, type SampleContent } from "@/lib/samples/catalog";
import { productPath } from "@/lib/localized-paths";

export type PublicSample = {
  id: string;
  name: string;
  slug: string;
  kind: string;
  content: SampleContent;
  assets: { id: string; role: string; url: string; variantId: string | null }[];
  variants: { id: string; name: string }[];
};
type Product = {
  id: string;
  name: string;
  slug: string;
  image: string;
  links: { id: string; variantId: string | null }[];
  variantIds: (string | null)[];
};
const copy = {
  nl: {
    materials: "Materialen",
    finishes: "Afwerkingen",
    title: "De basis voor jouw mooiste drukwerk.",
    finishTitle: "Het detail dat het verschil maakt.",
    intro:
      "Bekijk structuur, uitstraling en mogelijkheden. Ontdek welk materiaal bij jouw ontwerp past.",
    finishIntro:
      "Van subtiele bescherming tot een opvallend accent. Bekijk de afwerking van dichtbij en ontdek de bijpassende producten.",
    search: "Zoek in de collectie",
    view: "Ontdek dit materiaal",
    finishView: "Ontdek deze afwerking",
    detail: "Van dichtbij",
    photoFront: "Voorzijde",
    photoPrint: "Bedrukking",
    photoTexture: "Structuurzoom",
    photoBack: "Achterzijde",
    about: "Alles over",
    specs: "Eigenschappen",
    uses: "Waar gebruik je het voor?",
    care: "Goed om te weten",
    products: "Maak er iets moois van",
    productIntro: "Kies een product en stel jouw uitvoering samen.",
    all: "Alle varianten",
    compare: "Vergelijken",
    empty:
      "Binnenkort vind je hier onze materialen en afwerkingen, met foto’s van echte monsters.",
    choose: "Bekijk & ontwerp",
    photo: "Foto vergroten",
    close: "Sluiten",
  },
  en: {
    materials: "Materials",
    finishes: "Finishes",
    title: "The foundation of beautiful print.",
    finishTitle: "The detail that makes a difference.",
    intro:
      "Explore textures, appearance and possibilities. Find the right material for your design.",
    finishIntro:
      "From subtle protection to a striking accent. Explore finishes up close and discover matching products.",
    search: "Search the collection",
    view: "Explore this material",
    finishView: "Explore this finish",
    detail: "Up close",
    photoFront: "Front",
    photoPrint: "Print detail",
    photoTexture: "Texture zoom",
    photoBack: "Back",
    about: "All about",
    specs: "Properties",
    uses: "What can you use it for?",
    care: "Good to know",
    products: "Make something beautiful",
    productIntro: "Choose a product and configure your print.",
    all: "All variants",
    compare: "Compare",
    empty:
      "Our materials and finishes will appear here soon, with photos of real samples.",
    choose: "View & design",
    photo: "Enlarge photo",
    close: "Close",
  },
  de: {
    materials: "Materialien",
    finishes: "Veredelungen",
    title: "Die Basis für deine schönsten Druckprodukte.",
    finishTitle: "Das Detail, das den Unterschied macht.",
    intro:
      "Entdecke Struktur, Optik und Möglichkeiten. Finde das passende Material für dein Design.",
    finishIntro:
      "Von dezentem Schutz bis zum auffälligen Akzent. Entdecke Veredelungen im Detail und passende Produkte.",
    search: "Kollektion durchsuchen",
    view: "Material entdecken",
    finishView: "Veredelung entdecken",
    detail: "Im Detail",
    photoFront: "Vorderseite",
    photoPrint: "Druckdetail",
    photoTexture: "Strukturzoom",
    photoBack: "Rückseite",
    about: "Alles über",
    specs: "Eigenschaften",
    uses: "Wofür eignet es sich?",
    care: "Gut zu wissen",
    products: "Mach etwas Schönes daraus",
    productIntro: "Wähle ein Produkt und stelle deine Ausführung zusammen.",
    all: "Alle Varianten",
    compare: "Vergleichen",
    empty:
      "Hier findest du bald unsere Materialien und Veredelungen mit Fotos echter Muster.",
    choose: "Ansehen & gestalten",
    photo: "Foto vergrößern",
    close: "Schließen",
  },
  fr: {
    materials: "Matériaux",
    finishes: "Finitions",
    title: "La base de vos plus belles impressions.",
    finishTitle: "Le détail qui fait la différence.",
    intro:
      "Découvrez les textures, les rendus et les possibilités. Trouvez le matériau adapté à votre création.",
    finishIntro:
      "De la protection discrète à la touche remarquable. Découvrez les finitions en détail et les produits associés.",
    search: "Rechercher dans la collection",
    view: "Découvrir ce matériau",
    finishView: "Découvrir cette finition",
    detail: "De près",
    photoFront: "Recto",
    photoPrint: "Détail imprimé",
    photoTexture: "Zoom texture",
    photoBack: "Verso",
    about: "Tout savoir sur",
    specs: "Caractéristiques",
    uses: "Pour quelles utilisations ?",
    care: "Bon à savoir",
    products: "Créez quelque chose de beau",
    productIntro: "Choisissez un produit et configurez votre impression.",
    all: "Toutes les variantes",
    compare: "Comparer",
    empty:
      "Retrouvez bientôt nos matériaux et finitions avec des photos de vrais échantillons.",
    choose: "Voir et créer",
    photo: "Agrandir la photo",
    close: "Fermer",
  },
};
const specificationLabels: Record<string, Record<string, string>> = {
  Gewicht: { en: 'Weight', de: 'Grammatur', fr: 'Grammage' },
  'Gefotografeerde variant': { en: 'Photographed variant', de: 'Fotografierte Variante', fr: 'Variante photographiée' },
  'Gefotografeerde varianten': { en: 'Photographed variants', de: 'Fotografierte Varianten', fr: 'Variantes photographiées' },
  Samenstelling: { en: 'Composition', de: 'Zusammensetzung', fr: 'Composition' },
  Oppervlak: { en: 'Surface', de: 'Oberfläche', fr: 'Surface' },
};

function specificationLabel(label: string, locale: string): string {
  return specificationLabels[label]?.[locale] || label;
}
export function SampleCatalog({
  entries,
  selected,
  products = [],
  locale,
  kind,
}: {
  entries: PublicSample[];
  selected?: PublicSample;
  products?: Product[];
  locale: string;
  kind: string;
}) {
  const lang = locale in copy ? (locale as keyof typeof copy) : "nl",
    t = copy[lang];
  const [query, setQuery] = useState(""),
    [variant, setVariant] = useState(""),
    [photoId, setPhotoId] = useState(""),
    [large, setLarge] = useState(false),
    [imageZoom, setImageZoom] = useState(1),
    [comparison, setComparison] = useState<string[]>([]);
  const zoomBox = useRef<HTMLDivElement>(null);
  const panStart = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const base = `/${locale}/${kind === "finish" ? "afwerkingen" : "materialen"}`;
  const label = kind === "finish" ? t.finishes : t.materials;
  const content =
    selected?.content.translations[lang] || selected?.content.translations.nl;
  const title = content?.name || selected?.name;
  const photographedVariants = selected
    ? photographedSampleVariants(selected.variants, selected.assets)
    : [];
  const assets =
    selected?.assets.filter(
      (a) => !variant || !a.variantId || a.variantId === variant,
    ).sort((a, b) => {
      const rank = (role: string) => ({ detail: 0, print: 1, overview: 2, option: 3 }[role] ?? 4);
      return rank(a.role) - rank(b.role);
    }) || [];
  const photo =
    assets.find((a) => a.id === photoId) ||
    (variant
      ? assets.find(
          (a) => a.variantId === variant && a.role === "presentation",
        ) ||
        assets.find((a) => a.variantId === variant && a.role === "detail") ||
        assets.find((a) => a.variantId === variant && a.role === "overview") ||
        assets.find((a) => a.variantId === variant)
      : undefined) ||
    assets.find((a) => a.role === "presentation") ||
    assets.find((a) => a.role === "detail") ||
    assets.find((a) => a.role === "overview") ||
    assets[0];
  const filtered = entries.filter((e) =>
    `${e.name} ${e.content.translations[lang]?.intro || e.content.translations.nl.intro}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  useEffect(() => {
    if (!large || imageZoom <= 1) return;
    const frame = requestAnimationFrame(() => {
      const box = zoomBox.current;
      if (!box) return;
      box.scrollLeft = (box.scrollWidth - box.clientWidth) / 2;
      box.scrollTop = (box.scrollHeight - box.clientHeight) / 2;
    });
    return () => cancelAnimationFrame(frame);
  }, [large, imageZoom, photo?.id]);
  return (
    <div className="bg-white text-slate-900">
      <div className="mx-auto max-w-7xl px-5 py-7 sm:px-8">
        <nav className="mb-8 flex gap-2 text-xs text-slate-500">
          <a href={`/${locale}`}>Printen.eu</a>
          <span>/</span>
          <a href={base}>{label}</a>
          {selected && (
            <>
              <span>/</span>
              <span>{title}</span>
            </>
          )}
        </nav>
        {selected ? (
          <>
            <section className="grid min-w-0 grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
              <div className="min-w-0">
                <button
                  className="relative block aspect-square w-full max-w-full overflow-hidden rounded-2xl bg-[#f6f5f2]"
                  onClick={() => { setImageZoom(1); setLarge(true); }}
                  aria-label={t.photo}
                >
                  {photo && (
                    <img
                      src={photo.url}
                      alt={title}
                      className="h-full w-full max-w-full object-contain"
                    />
                  )}
                  <span className="absolute bottom-4 right-4 rounded-full bg-white px-4 py-2 text-xs shadow-sm">
                    {t.photo} ↗
                  </span>
                </button>
                <div className="mt-3 flex max-w-full gap-3 overflow-x-auto">
                  {assets.map((a) => (
                    <button
                      aria-label={({ detail: t.photoFront, print: t.photoPrint, option: t.photoTexture, overview: t.photoBack } as Record<string, string>)[a.role] || `${t.detail} ${a.role}`}
                      key={a.id}
                      onClick={() => setPhotoId(a.id)}
                      className={`w-24 shrink-0 overflow-hidden rounded-xl border-2 bg-white ${photo?.id === a.id ? "border-blue-600" : "border-transparent"}`}
                    >
                      <img
                        src={a.url}
                        alt=""
                        className="h-20 w-full object-cover"
                      />
                      <span className="block truncate px-1 py-1 text-center text-[11px] font-medium text-slate-700">
                        {({ detail: t.photoFront, print: t.photoPrint, option: t.photoTexture, overview: t.photoBack } as Record<string, string>)[a.role] || a.role}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex min-w-0 flex-col justify-center">
                <p className="text-xs font-bold uppercase tracking-[.18em] text-blue-600">
                  {label} · Printen.eu
                </p>
                <h1 className="mt-4 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
                  {title}
                </h1>
                <p className="mt-5 text-lg leading-relaxed text-slate-600">
                  {content?.intro}
                </p>
                {photographedVariants.length > 1 && (
                  <label className="mt-6 text-sm font-semibold">
                    {t.all}
                    <select
                      className="mt-2 block w-full rounded-xl border p-3 font-normal"
                      value={variant}
                      onChange={(e) => {
                        setVariant(e.target.value);
                        setPhotoId("");
                      }}
                    >
                      <option value="">{t.all}</option>
                      {sortSampleVariants(photographedVariants).map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <div className="mt-7 flex flex-wrap gap-3">
                  <a
                    href="#sample-products"
                    className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white hover:bg-blue-700"
                  >
                    {t.choose} →
                  </a>
                  <a
                    href="#sample-specs"
                    className="rounded-xl border px-6 py-3 text-sm font-semibold"
                  >
                    {t.specs}
                  </a>
                </div>
                <div className="mt-8 flex items-center gap-4 border-t pt-6">
                  {assets.find((a) => a.role === "detail") && (
                    <img
                      src={assets.find((a) => a.role === "detail")!.url}
                      alt={t.detail}
                      className="h-16 w-16 rounded-full object-cover"
                    />
                  )}
                  <p className="text-sm font-semibold">
                    {t.detail}
                    <span className="mt-1 block font-normal text-slate-500">
                      {title}
                    </span>
                  </p>
                </div>
              </div>
            </section>
            <div className="my-12 flex flex-wrap gap-6 border-y py-4 text-sm font-semibold">
              <a href="#sample-about">
                {t.about} {title}
              </a>
              <a href="#sample-specs">{t.specs}</a>
              <a href="#sample-products">{t.products}</a>
            </div>
            <section className="grid min-w-0 grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
              <article id="sample-about">
                <h2 className="text-2xl font-bold">
                  {t.about} {title}
                </h2>
                <p className="mt-4 whitespace-pre-line leading-7 text-slate-600">
                  {content?.description}
                </p>
                {content?.applications && (
                  <>
                    <h3 className="mt-8 text-xl font-bold">{t.uses}</h3>
                    <p className="mt-3 whitespace-pre-line leading-7 text-slate-600">
                      {content.applications}
                    </p>
                  </>
                )}
                {content?.care && (
                  <>
                    <h3 className="mt-8 text-xl font-bold">{t.care}</h3>
                    <p className="mt-3 whitespace-pre-line leading-7 text-slate-600">
                      {content.care}
                    </p>
                  </>
                )}
              </article>
              <aside
                id="sample-specs"
                className="h-fit rounded-2xl bg-slate-50 p-7"
              >
                <h2 className="text-xl font-bold">{t.specs}</h2>
                <dl className="mt-5 divide-y">
                  {selected.content.specs.map((s) => (
                    <div
                      key={s.label}
                      className="grid grid-cols-2 gap-4 py-3 text-sm"
                    >
                      <dt className="text-slate-500">{specificationLabel(s.label, lang)}</dt>
                      <dd className="font-medium">{s.value}</dd>
                    </div>
                  ))}
                </dl>
                {photographedVariants.length > 0 && (
                  <h3 className="mt-6 text-sm font-semibold text-slate-500">
                    {specificationLabel(
                      photographedVariants.length > 1
                        ? "Gefotografeerde varianten"
                        : "Gefotografeerde variant",
                      lang,
                    )}
                  </h3>
                )}
                {sortSampleVariants(photographedVariants).map((v) => (
                  <p
                    className="mt-3 rounded-lg bg-white p-3 text-sm"
                    key={v.id}
                  >
                    {v.name}
                  </p>
                ))}
              </aside>
            </section>
            <section id="sample-products" className="py-16">
              <h2 className="text-3xl font-bold tracking-tight">
                {t.products}
              </h2>
              <p className="mt-3 text-slate-500">{t.productIntro}</p>
              <div className="mt-7 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
                {products
                  .filter(
                    (p) =>
                      !variant ||
                      p.variantIds.includes(variant) ||
                      p.variantIds.includes(null),
                  )
                  .map((p) => (
                    <a
                      key={p.id}
                      className="overflow-hidden rounded-2xl border transition hover:-translate-y-1 hover:shadow-md"
                      href={`${productPath(locale, p.slug)}?sample=${encodeURIComponent(sampleProductLink(p.links, variant) || "")}`}
                    >
                      <div className="aspect-square bg-slate-50">
                        {p.image && (
                          <img
                            src={p.image}
                            alt={p.name}
                            className="h-full w-full object-contain"
                          />
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold">{p.name}</h3>
                        <p className="mt-3 text-sm font-semibold text-blue-600">
                          {t.choose} →
                        </p>
                      </div>
                    </a>
                  ))}
              </div>
            </section>
          </>
        ) : (
          <>
            <section className="rounded-2xl bg-[#f5f7fc] px-6 py-12 sm:px-12">
              <p className="text-xs font-bold uppercase tracking-[.18em] text-blue-600">
                {label} · Printen.eu
              </p>
              <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
                {kind === "finish" ? t.finishTitle : t.title}
              </h1>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
                {kind === "finish" ? t.finishIntro : t.intro}
              </p>
              <div className="mt-8 flex gap-3">
                <a
                  className="rounded-full border bg-white px-5 py-2 text-sm font-semibold"
                  href={`/${locale}/materialen`}
                >
                  {t.materials}
                </a>
                <a
                  className="rounded-full border bg-white px-5 py-2 text-sm font-semibold"
                  href={`/${locale}/afwerkingen`}
                >
                  {t.finishes}
                </a>
              </div>
            </section>
            <input
              className="my-8 w-full rounded-xl border px-5 py-3 sm:max-w-md"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.search}
              aria-label={t.search}
            />
            {!entries.length && (
              <p className="pb-12 text-slate-500">{t.empty}</p>
            )}
            <div className="grid gap-6 pb-12 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((e) => {
                const text =
                  e.content.translations[lang] || e.content.translations.nl;
                const asset =
                  e.assets.find((a) => a.role === "presentation") ||
                  e.assets.find((a) => a.role === "detail") ||
                  e.assets.find((a) => a.role === "overview");
                return (
                  <article
                    className="overflow-hidden rounded-2xl border"
                    key={e.id}
                  >
                    <a href={`${base}/${e.slug}`}>
                      <div className="aspect-[4/3] bg-[#f6f5f2]">
                        {asset && (
                          <img
                            src={asset.url}
                            alt={text.name || e.name}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="p-5">
                        <h2 className="text-xl font-bold">
                          {text.name || e.name}
                        </h2>
                        <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-500">
                          {text.intro}
                        </p>
                        <p className="mt-4 text-sm font-semibold text-blue-600">
                          {kind === "finish" ? t.finishView : t.view} →
                        </p>
                      </div>
                    </a>
                    <label className="flex items-center gap-2 border-t px-5 py-3 text-xs">
                      <input
                        type="checkbox"
                        checked={comparison.includes(e.id)}
                        disabled={
                          !comparison.includes(e.id) && comparison.length >= 3
                        }
                        onChange={(ev) =>
                          setComparison(
                            ev.target.checked
                              ? [...comparison, e.id]
                              : comparison.filter((id) => id !== e.id),
                          )
                        }
                      />
                      {t.compare}
                    </label>
                  </article>
                );
              })}
            </div>
            {comparison.length > 0 && (
              <section className="mb-10 overflow-x-auto rounded-xl border p-5">
                <h2 className="mb-5 text-xl font-bold">{t.compare}</h2>
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th className="p-3">{t.specs}</th>
                      {entries
                        .filter((e) => comparison.includes(e.id))
                        .map((e) => (
                          <th className="p-3" key={e.id}>
                            {e.content.translations[lang]?.name || e.name}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(
                      new Set(
                        entries
                          .filter((e) => comparison.includes(e.id))
                          .flatMap((e) => e.content.specs.map((s) => s.label)),
                      ),
                    ).map((label) => (
                      <tr className="border-t" key={label}>
                        <th className="p-3 font-normal text-slate-500">
                          {specificationLabel(label, lang)}
                        </th>
                        {entries
                          .filter((e) => comparison.includes(e.id))
                          .map((e) => (
                            <td className="p-3" key={e.id}>
                              {e.content.specs.find((s) => s.label === label)
                                ?.value || "—"}
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </>
        )}
        <Dialog open={large} onOpenChange={setLarge}>
          <DialogContent className="max-w-[95vw] border-0 bg-white p-5 sm:max-w-5xl">
            <DialogTitle className="sr-only">
              {title} - {t.photo}
            </DialogTitle>
            {photo && (
              <div>
                <div className="mb-3 flex flex-wrap items-center gap-2" aria-label="Foto zoomen">
                  {[1, 1.5, 2, 3, 4].map((level) => (
                    <button key={level} type="button" onClick={() => setImageZoom(level)}
                      aria-pressed={imageZoom === level}
                      className={`rounded-full px-3 py-1 text-sm font-semibold ${imageZoom === level ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                      {level * 100}%
                    </button>
                  ))}
                  <span className="ml-2 hidden text-xs text-slate-500 sm:inline">Scroll om te zoomen · sleep om te bewegen</span>
                </div>
                <div ref={zoomBox} className="max-h-[78vh] cursor-grab overflow-auto rounded-xl bg-[#f6f5f2] active:cursor-grabbing"
                  onWheel={(event) => {
                    event.preventDefault();
                    setImageZoom((current) => Math.min(4, Math.max(1, Math.round((current + (event.deltaY < 0 ? 0.25 : -0.25)) * 4) / 4)));
                  }}
                  onPointerDown={(event) => {
                    const box = zoomBox.current;
                    if (!box) return;
                    panStart.current = { x: event.clientX, y: event.clientY, left: box.scrollLeft, top: box.scrollTop };
                    box.setPointerCapture(event.pointerId);
                  }}
                  onPointerMove={(event) => {
                    const box = zoomBox.current, start = panStart.current;
                    if (!box || !start) return;
                    box.scrollLeft = start.left - (event.clientX - start.x);
                    box.scrollTop = start.top - (event.clientY - start.y);
                  }}
                  onPointerUp={() => { panStart.current = null; }}
                  onPointerCancel={() => { panStart.current = null; }}>
                  <img src={photo.url} alt={title}
                    className="h-auto max-w-none"
                    style={{ width: `${imageZoom * 100}%` }} />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
